import type {
    UserStatusResponse,
    StatusShift,
    ClockRole,
    PunchResponse
} from '@src/api/punchTypes';
import { useTerminalAccount } from '@src/context/AccountContext';
import { useTranslation } from '@src/i18n';
import { ShiftCard } from './ShiftCard';
import { ShiftEmptyState } from './ShiftEmptyState';
import './ShiftPanel.css';

function getShift(response: UserStatusResponse): StatusShift | null {
    if (response.status === 'clocked_out')
        return response.preferredShift?.shift ?? null;
    return response.currentShift;
}

function getOtherAssignedRoles(response: UserStatusResponse): ClockRole[] {
    if (response.status === 'clocked_out')
        return response.preferredShift?.otherAssignedRoles ?? [];
    return [];
}

interface ShiftPanelProps {
    response: UserStatusResponse;
    loadingLabel: string | null;
    onAction: (execute: () => Promise<PunchResponse>, label: string) => void;
}

export function ShiftPanel({
    response,
    loadingLabel,
    onAction
}: ShiftPanelProps) {
    const { account } = useTerminalAccount();
    const { t } = useTranslation();
    const shift = getShift(response);
    const otherAssignedRoles = getOtherAssignedRoles(response);
    const title =
        response.status === 'clocked_out'
            ? t('userStatus.yourShifts')
            : t('userStatus.currentShift');
    const locationTimezone = account?.locationTimezone ?? 'UTC';
    const dateStr = new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: locationTimezone
    }).format(new Date());

    return (
        <div className="shift-panel" data-testid="shift-panel">
            <div className="shift-panel__header">
                <p className="shift-panel__title">{title}</p>
                <p className="shift-panel__date">{dateStr}</p>
            </div>
            <div className="shift-panel__card-box">
                {shift ? (
                    <ShiftCard
                        shift={shift}
                        availableActions={response.availableActions}
                        loadingLabel={loadingLabel}
                        locationTimezone={locationTimezone}
                        onAction={onAction}
                        otherAssignedRoles={otherAssignedRoles}
                    />
                ) : (
                    <ShiftEmptyState
                        availableActions={response.availableActions}
                        loadingLabel={loadingLabel}
                        onAction={onAction}
                    />
                )}
            </div>
        </div>
    );
}
